/**
 * @file order-delivery-view.ts
 * @description Сведение данных для вкладки «Доставка» (phase19.24 шаг 5.2).
 *
 * Чистые функции без React. Всё, что нужно вкладке, приходит одним ответом
 * `GET /admin/orders/:id/delivery` (условия, право на правку, заявка,
 * перевозчики с расчётом). Здесь — запасные пути на случай, когда этого ответа
 * нет: без них упавший запрос оставлял бы вкладку пустой, хотя заказ и заявка
 * у страницы уже загружены.
 *
 * Правило выбора одно: ответ сервера первичен, запасной путь считается ТОЛЬКО
 * из настоящих полей заказа (источник заказа, дата заявки, полученные деньги).
 * Ничего не выдумываем: пустое поле честнее подставленного.
 */

import type {
  DeliveryAgreementDto,
  DeliveryLockReason,
  OrderDeliveryReceiptDto,
} from "./api-delivery-agreement";
import type { OrderDeliveryDto, OrderDto, ShipmentCarrier } from "./api-orders";
import {
  DELIVERY_BLOCK_STATE_META,
  deliveryBlockBadge,
  deliveryBlockText,
  resolveDeliveryBlockState,
  type DeliveryBlockState,
  type DeliveryBlockStateMeta,
} from "./delivery-block-state";

/**
 * Снапшот условий доставки заказа.
 *
 * Обычно приходит прямо в ответе о доставке. Запасной путь — для случая, когда
 * этот ответ не пришёл вовсе: тогда условия читаются из заявки и дополняются
 * тем, что известно точно, — источником заказа (витрина или менеджер) и датой,
 * когда условия выставляли. Без запасного пути упавший запрос оставлял бы
 * вкладку пустой, хотя согласованные условия у страницы уже есть.
 */
export function orderDeliveryReceipt(
  order: OrderDto,
  delivery: OrderDeliveryDto | null,
  agreement: DeliveryAgreementDto | null,
): OrderDeliveryReceiptDto | null {
  if (delivery?.receipt) return delivery.receipt;
  const receipt = agreement?.receipt;
  if (!receipt) return null;
  return {
    ...receipt,
    quote: null,
    source: order.source === "SITE" ? "SITE" : "MANAGER",
    setAt: agreement?.calculatedAt ?? agreement?.requestedAt ?? order.createdAt,
  };
}

/**
 * Перевозчик отгрузки по согласованным условиям — тот же выбор, что делает
 * сервер при префилле (`shipments.service`).
 *
 * `SHOP_COURIER` → `CUSTOM`: курьер магазина везёт сам, перевозчика из
 * справочника у него нет, и «везём сами» в отгрузке называется именно так.
 * `null` — условий нет, менеджер выбирает перевозчика руками (иначе сервер
 * ответит `shipment_carrier_required`).
 */
export function shipmentCarrierForReceipt(
  receipt: OrderDeliveryReceiptDto | null,
): ShipmentCarrier | null {
  if (!receipt) return null;
  if (receipt.method === "SHOP_COURIER") return "CUSTOM";
  if (receipt.method === "PICKUP") return "PICKUP";
  return receipt.carrier;
}

/** Можно ли менять условия и почему нельзя. */
export interface OrderDeliveryLock {
  canEdit: boolean;
  reason: DeliveryLockReason | null;
}

/**
 * Право на правку условий: ответ сервера, а без него — те же правила клиентом.
 *
 * Деньги получены — условия заморожены (F26): разницу проводят доплатой или
 * возвратом. Закрытый заказ и роль без права вести заказы правку тоже
 * закрывают, но по другим причинам — их различает реестр состояний.
 */
export function orderDeliveryLock(
  order: OrderDto,
  delivery: OrderDeliveryDto | null,
  canManage: boolean,
): OrderDeliveryLock {
  if (delivery?.canEdit !== undefined || delivery?.lockReason !== undefined) {
    const reason = delivery.lockReason ?? null;
    return { canEdit: delivery.canEdit ?? reason === null, reason };
  }
  if (!canManage) return { canEdit: false, reason: "no_permission" };
  const kind = order.stage?.kind;
  const closed = kind
    ? kind === "COMPLETED" || kind === "CANCELLED"
    : order.status === "COMPLETED" || order.status === "CANCELLED";
  if (closed) return { canEdit: false, reason: "closed" };
  const paid = order.payment?.paidTotal ?? 0;
  if (paid > 0) {
    const total = Number(order.totalGross);
    return {
      canEdit: false,
      reason: Number.isFinite(total) && paid + 0.005 >= total ? "paid" : "partially_paid",
    };
  }
  return { canEdit: true, reason: null };
}

/**
 * Сумма товаров заказа (без доставки) строкой — база всех итогов блока.
 *
 * Заявка считает её сама; без заявки вычитаем позицию-услугу «Доставка» из
 * суммы заказа — иначе доставка попала бы в итог дважды.
 */
export function orderItemsTotal(
  order: OrderDto,
  delivery: OrderDeliveryDto | null,
  agreement: DeliveryAgreementDto | null,
): string {
  if (agreement?.totals.itemsTotal) return agreement.totals.itemsTotal;
  const total = Number(order.totalGross);
  if (!Number.isFinite(total)) return "0";
  const deliveryPrice = delivery?.deliveryItem?.priceGross ?? 0;
  return String(Math.round((total - deliveryPrice) * 100) / 100);
}

/** Всё, что блок «Доставка» показывает по одному ответу сервера. */
export interface DeliveryBlockView {
  /** Заявка: из ответа о доставке, а без него — карточная (запасной путь). */
  agreement: DeliveryAgreementDto | null;
  receipt: OrderDeliveryReceiptDto | null;
  state: DeliveryBlockState;
  meta: DeliveryBlockStateMeta;
  /** Подпись бейджа: при стадийном запрете — по стадии заказа. */
  badge: string | null;
  /** Объясняющая строка: у стадийного запрета тоже зависит от стадии. */
  text: string;
  /** Сумма товаров (без доставки) — база итогов формы. */
  itemsTotal: string;
}

/**
 * Сведение вкладки «Доставка»: что показать по заказу и ответу о доставке.
 *
 * Отдельно от компонента, потому что это решение, а не раскладка: состояние
 * блока, бейдж и суммы считаются из одних и тех же данных, и раньше эти
 * четыре строки жили в теле компонента, растя его за лимит 200 строк.
 */
export function deliveryBlockView(
  order: OrderDto,
  delivery: OrderDeliveryDto | null,
  cardAgreement: DeliveryAgreementDto | null,
  options: { canManage: boolean; editing: boolean; quoteReason: string | null },
): DeliveryBlockView {
  const agreement = delivery?.agreement ?? cardAgreement;
  const receipt = orderDeliveryReceipt(order, delivery, agreement);
  const lock = orderDeliveryLock(order, delivery, options.canManage);
  const stageKind = order.stage?.kind ?? null;
  const state = resolveDeliveryBlockState({
    agreement,
    receipt,
    source: receipt?.source ?? null,
    canEdit: lock.canEdit,
    lockReason: lock.reason,
    nextStep: delivery?.nextStep ?? null,
    stageKind,
    editing: options.editing,
    quoteReason: options.quoteReason,
  });
  return {
    agreement,
    receipt,
    state,
    meta: DELIVERY_BLOCK_STATE_META[state],
    badge: deliveryBlockBadge(state, stageKind),
    text: deliveryBlockText(state, stageKind),
    itemsTotal: orderItemsTotal(order, delivery, agreement),
  };
}
